/**
 * API functions for extension release management.
 */

import { ReleaseInfo } from "./types";

// Use same API URL pattern as other API modules
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

/**
 * Fallback release info when no release is available.
 * Used to gracefully handle 404 responses.
 */
export function createNoReleaseInfo(): ReleaseInfo {
  return {
    latest_version: "0.0.0",
    min_required_version: "0.0.0",
    download_url: "",
    release_date: "",
    channel: "prod",
  };
}

/**
 * Fetch release info from the backend (prod channel only).
 *
 * @param requestApi - The authenticated request API instance
 * @returns Release information including latest version and download URL.
 *          Returns fallback info (version 0.0.0) if no release exists (404).
 * @throws {Error} For other API errors
 */
export async function fetchReleaseInfo(requestApi: {
  get: (url: string, params?: any) => Promise<{ data: any }>;
}): Promise<ReleaseInfo> {
  const url = `${API_BASE_URL}/extension/release-info`;

  console.info("[Extension API] Fetching release info", { url });

  try {
    const response = await requestApi.get(url);

    console.info("[Extension API] Release info received", {
      latestVersion: response.data?.latest_version,
      minRequiredVersion: response.data?.min_required_version,
      hasDownloadUrl: !!response.data?.download_url,
    });

    return response.data;
  } catch (error: any) {
    // Handle 404 - no release available for this channel yet
    const status = error?.response?.status || error?.status;
    if (status === 404) {
      console.info("[Extension API] No release available (404)");
      // Return fallback info instead of throwing
      // This allows the UI to function normally when no release exists
      return createNoReleaseInfo();
    }

    // Log other errors with context
    console.error("[Extension API] Failed to fetch release info", {
      status,
      error: error?.message || error,
    });

    // Re-throw other errors
    throw error;
  }
}
